import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { OrganizationService } from '../../lib/api/organization.services'
import { organizationSlugSchema } from '../../lib/schemas/user.schema'
import { getErrorData } from '../../lib/utils/handleApiError'
import { useDebounce } from './useDebounce'

const normalizeSlug = (slug: string) =>
	slug.trim().toLowerCase().replace(/\s+/g, '-')

interface Options {
	slug: string
	currentSlug?: string
	enabled?: boolean
	mode?: 'onChange' | 'onSubmit'
}

export const useOrganizationSlugValidation = ({
	slug,
	currentSlug,
	enabled = true,
	mode = 'onChange',
}: Options) => {
	const normalizedSlug = normalizeSlug(slug)
	const [submittedSlug, setSubmittedSlug] = useState<string | null>(null)
	const showValidation = mode === 'onChange' || submittedSlug === normalizedSlug
	const debouncedSlug = useDebounce(normalizedSlug, 500)
	const validation = organizationSlugSchema.safeParse(normalizedSlug)
	const isUnchanged = Boolean(currentSlug) && normalizedSlug === normalizeSlug(currentSlug ?? '')
	const needsCheck = validation.success && !isUnchanged
	const queryKey = ['organizationSlugAvailability', normalizedSlug]
	const queryClient = useQueryClient()
	const query = useQuery({
		queryKey,
		queryFn: () => OrganizationService.checkOrganizationSlug({ slug: normalizedSlug }),
		enabled: enabled && mode === 'onChange' && needsCheck && normalizedSlug === debouncedSlug,
		retry: false,
	})
	const isChecking =
		enabled && needsCheck &&
		(query.isFetching || (
			mode === 'onChange' && (normalizedSlug !== debouncedSlug || query.isPending)
		))
	const isAvailable =
		isUnchanged || (
			needsCheck && !isChecking && query.isSuccess &&
			query.data.success && query.data.data === false
		)

	let error: string | undefined
	if (enabled && showValidation && !isUnchanged && !validation.success) {
		error = validation.error.issues[0]?.message
	} else if (enabled && showValidation && needsCheck && !isChecking) {
		if (query.isError || (query.data && !query.data.success)) {
			error = 'Unable to check slug availability. Please try again.'
		} else if (query.data?.data === true) {
			error = 'This slug is already used'
		}
	}

	const validate = async () => {
		setSubmittedSlug(normalizedSlug)
		if (isUnchanged) return true
		if (!enabled || !validation.success) return false
		const result = await query.refetch()
		return result.isSuccess && result.data.success && result.data.data === false
	}

	const handleSaveError = (error: unknown) => {
		if (getErrorData(error).errorMessageCode === 'ORGANIZATION_SLUG_ALREADY_EXISTS') {
			queryClient.setQueryData(queryKey, { success: true, data: true })
		}
	}

	return { normalizedSlug, error, isChecking, isAvailable, validate, handleSaveError }
}
